import { getLevelForm, type IcfFormLevel, type StatementRatingValue } from '../../data/icfFormStatements';
import type { SessionObservationForm, SofAiRating, SofEvidenceFeedback, SofMentorRating, SofUpdateRequest } from '../../types/mentorEngagements';

/** PCC API records can carry BARS labels; the boolean is authoritative. */
export function effectiveRating(ai: SofAiRating | undefined, mentor: SofMentorRating | null | undefined, level: IcfFormLevel): {
  value: StatementRatingValue | null; confirmedBy: 'mentor' | 'ai' | 'none';
} {
  const valueOf = (rating?: SofMentorRating | null) => {
    if (level === 'pcc' && typeof rating?.observed === 'boolean') return rating.observed ? 'observed' : 'not_observed';
    const value = rating?.rating_label;
    return getLevelForm(level).scale.options.some(option => option.value === value) ? value! : null;
  };
  const override = valueOf(mentor);
  if (override !== null) return { value: override, confirmedBy: 'mentor' };
  const suggestion = valueOf(ai);
  return { value: suggestion, confirmedBy: suggestion === null ? 'none' : 'ai' };
}

export type ReviewState = 'unrated' | 'ai_suggested' | 'confirmed' | 'overridden';

/**
 * Derived, never stored: what the mentor has done with the AI suggestion.
 * A mentor rating with no AI value (manual sessions) counts as `confirmed`,
 * not `overridden` — there was nothing to override.
 */
export function deriveReviewState(ai: SofAiRating | undefined, mentor: SofMentorRating | null | undefined, level: IcfFormLevel): {
  state: ReviewState; value: StatementRatingValue | null; aiValue: StatementRatingValue | null;
} {
  const aiValue = effectiveRating(ai, undefined, level).value;
  const rating = effectiveRating(ai, mentor, level);
  if (rating.confirmedBy === 'none') return { state: 'unrated', value: null, aiValue };
  if (rating.confirmedBy === 'ai') return { state: 'ai_suggested', value: rating.value, aiValue };
  return { state: aiValue !== null && rating.value !== aiValue ? 'overridden' : 'confirmed', value: rating.value, aiValue };
}

export function sofReviewSummary(form: Pick<SessionObservationForm, 'ai_ratings' | 'mentor_ratings'>, level: IcfFormLevel) {
  const summary = { total: 0, aiSuggested: 0, mentorConfirmed: 0, unrated: 0 };
  for (const competency of getLevelForm(level).competencies) {
    for (const statement of competency.statements) {
      summary.total++;
      const rating = effectiveRating(form.ai_ratings[statement.id], form.mentor_ratings[statement.id], level);
      if (rating.confirmedBy === 'mentor') summary.mentorConfirmed++;
      else if (rating.confirmedBy === 'ai') summary.aiSuggested++;
      else summary.unrated++;
    }
  }
  return summary;
}

export interface SofReviewState {
  /** Only locally touched statements. `null` asks the server to drop a stored override. */
  mentorRatings: Record<string, SofMentorRating | null>;
  c1: Pick<SofUpdateRequest, 'c1_q1' | 'c1_q2' | 'c1_comment'>;
  feedback: SessionObservationForm['feedback'];
  /** Locally touched relevance marks keyed by `citation_key`. */
  evidenceFeedback?: Record<string, SofEvidenceFeedback>;
}

export function buildSofUpdate(state: SofReviewState, level: IcfFormLevel): SofUpdateRequest {
  const mentor_ratings: Record<string, SofMentorRating | null> = {};
  for (const [id, rating] of Object.entries(state.mentorRatings)) {
    if (rating === null) { mentor_ratings[id] = null; continue; }
    const value = effectiveRating(undefined, rating, level).value;
    mentor_ratings[id] = level === 'pcc' && value !== null
      ? { observed: value === 'observed', comment: rating.comment ?? null, ...(rating.override_reason ? { override_reason: rating.override_reason } : {}) }
      : { ...rating };
  }
  const marks = Object.values(state.evidenceFeedback ?? {});
  return { mentor_ratings, ...state.c1, feedback: state.feedback, ...(marks.length ? { evidence_feedback: marks } : {}) };
}
